import {
  CheckOutlined,
  CheckCircleOutlined,
  FileSearchOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  Progress,
  Row,
  Space,
  Skeleton,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ICustomerSatisfactionActorStatistic,
  ICustomerSatisfactionOperationalSignalStatistic,
  ICustomerSatisfactionReasonStatistic,
  ICustomerSatisfactionServicePointStatistic,
  ICustomerSatisfactionStatistic,
  ICustomerSatisfactionTimeSeriesItem,
  ICustomerSatisfactionUnsatisfiedOrder,
} from "../../../models/customer-satisfaction/response";
import {
  IApiLoadState,
  IStatisticCacheMeta,
} from "../../../models/statistcs/cache";
import EndPoints from "../../../services/end-points";
import getApiErrorMessage from "../../../utils/api/get-api-error-message";
import OperationalSignalTags from "../../customer-satisfaction/operational-signal-tags";
import RefreshBtn from "../../general/table-components/actions/refresh-btn";
import styles from "./style.module.scss";

const { RangePicker } = DatePicker;

const DATE_FORMAT = "YYYY-MM-DD";
type DateField = "startDate" | "endDate";
type FieldErrors = Partial<Record<DateField, string>>;

const getNumber = (value?: number | null) =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const CustomerSatisfactionStats = () => {
  const { t, i18n } = useTranslation();
  const [statsState, setStatsState] = useState<
    IApiLoadState<ICustomerSatisfactionStatistic>
  >({ status: "loading" });
  const [startDate, setStartDate] = useState<string | undefined>();
  const [endDate, setEndDate] = useState<string | undefined>();
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const pollingRef = useRef<number>();
  const requestRef = useRef(0);
  const cacheRef = useRef<Record<string, ICustomerSatisfactionStatistic>>({});
  const stats = statsState.data;
  const loading = statsState.status === "loading";
  const isPending = statsState.status === "pending" || stats?.pending === true;
  const isInitialLoading = loading && !stats;
  const hasReadyStats = Boolean(stats && stats.pending !== true);

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }),
    [i18n.language],
  );

  const currencyFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 0 }),
    [i18n.language],
  );

  const getStatisticsErrorMessage = useCallback(
    (error: any) => {
      const statusCode = error?.response?.status;

      if (statusCode === 401) return t("statistics_session_expired");
      if (statusCode === 403) return t("statistics_permission_denied");
      if (statusCode === 422) {
        return getApiErrorMessage(error, t("statistics_invalid_filters"));
      }

      return getApiErrorMessage(error, t("server_error_try_again"));
    },
    [t],
  );

  const clearPolling = useCallback(() => {
    if (pollingRef.current) {
      window.clearTimeout(pollingRef.current);
      pollingRef.current = undefined;
    }
  }, []);

  const extractFieldError = (error: any, field: DateField) => {
    const errors = error?.response?.data?.errors;
    const value = errors?.[field];

    if (Array.isArray(value)) return value.filter(Boolean).join(", ");
    if (typeof value === "string") return value;

    return undefined;
  };

  const validateDates = () => {
    const errors: FieldErrors = {};
    const today = dayjs();
    const start = startDate ? dayjs(startDate, DATE_FORMAT, true) : null;
    const end = endDate ? dayjs(endDate, DATE_FORMAT, true) : null;

    if (startDate && (!start?.isValid() || start.isAfter(today, "day"))) {
      errors.startDate = t("invalid_date");
    }

    if (endDate && (!end?.isValid() || end.isAfter(today, "day"))) {
      errors.endDate = t("invalid_date");
    }

    if (start?.isValid() && end?.isValid() && end.isBefore(start, "day")) {
      errors.endDate = t("end_date_before_start_date");
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const fetchStats = useCallback(
    async (
      range: { startDate?: string; endDate?: string } = {},
      showLoading = true,
    ) => {
      const requestId = requestRef.current + 1;
      requestRef.current = requestId;
      clearPolling();

      if (showLoading) {
        setStatsState((previous) => ({
          status: "loading",
          data: previous.data,
          cacheMeta: previous.cacheMeta,
        }));
      }

      try {
        const { data } = await EndPoints.statistics.getCustomerSatisfaction({
          startDate: range.startDate,
          endDate: range.endDate,
        });

        if (requestId !== requestRef.current) return;

        setStartDate(data.startDate || range.startDate);
        setEndDate(data.endDate || range.endDate);
        setFieldErrors({});

        if (data.pending && data.cacheMeta?.status === "pending") {
          const cachedData = data.cacheMeta.requestHash
            ? cacheRef.current[data.cacheMeta.requestHash]
            : undefined;

          setStatsState((previous) => ({
            status: "pending",
            data: cachedData || previous.data || data,
            cacheMeta: data.cacheMeta,
          }));

          const waitSeconds = Math.max(
            data.cacheMeta.retryAfterSeconds ?? 10,
            1,
          );
          pollingRef.current = window.setTimeout(() => {
            fetchStats(range, false);
          }, waitSeconds * 1000);

          return;
        }

        if (data.cacheMeta?.requestHash) {
          cacheRef.current[data.cacheMeta.requestHash] = data;
        }

        setStatsState({
          status: "ready",
          data,
          cacheMeta: data.cacheMeta,
        });
      } catch (error) {
        if (requestId !== requestRef.current) return;

        setFieldErrors({
          startDate: extractFieldError(error, "startDate"),
          endDate: extractFieldError(error, "endDate"),
        });
        setStatsState((previous) => ({
          status: "error",
          data: previous.data,
          cacheMeta: previous.cacheMeta,
          statusCode: error?.response?.status,
          message: getStatisticsErrorMessage(error),
        }));
      }
    },
    [clearPolling, getStatisticsErrorMessage],
  );

  useEffect(() => {
    fetchStats({});

    return () => {
      clearPolling();
      requestRef.current += 1;
    };
  }, [clearPolling, fetchStats]);

  const formatNumber = (value?: number | null) => {
    const number = getNumber(value);
    return number === null ? "-" : numberFormatter.format(number);
  };

  const formatPercent = (value?: number | null) => {
    const number = getNumber(value);
    return number === null ? "-" : `${numberFormatter.format(number)}%`;
  };

  const formatMinutes = (value?: number | null) => {
    const number = getNumber(value);
    return number === null
      ? "-"
      : `${numberFormatter.format(number)} ${t("minute_short")}`;
  };

  const formatMoney = (value?: number | null) => {
    const number = getNumber(value);
    return number === null ? "-" : currencyFormatter.format(number);
  };

  const formatCacheDate = (value?: string | null) =>
    value ? dayjs(value).format("YYYY-MM-DD HH:mm") : undefined;

  const formatDuration = (seconds?: number | null) => {
    if (!seconds) return undefined;

    if (seconds >= 3600 && seconds % 3600 === 0) {
      return `${seconds / 3600} ${t("hours")}`;
    }

    if (seconds >= 60 && seconds % 60 === 0) {
      return `${seconds / 60} ${t("minutes")}`;
    }

    return `${seconds} ${t("seconds")}`;
  };

  const disabledFutureDate = (date: Dayjs) =>
    date && date.isAfter(dayjs(), "day");

  const applyFilters = () => {
    if (!validateDates()) return;

    fetchStats({ startDate, endDate });
  };
  const filtersDirty = Boolean(
    stats &&
      ((startDate || undefined) !== (stats.startDate || undefined) ||
        (endDate || undefined) !== (stats.endDate || undefined)),
  );

  const summary = stats?.summary;
  const hasSmallSamples = [
    ...(stats?.reasons || []),
    ...(stats?.servicePoints || []),
    ...(stats?.drivers || []),
    ...(stats?.investors || []),
  ].some((item) => item.smallSample);

  const renderRate = (value?: number | null) => {
    const number = getNumber(value);
    if (number === null) return "-";

    return (
      <Space direction="vertical" size={2} style={{ width: "100%" }}>
        <span>{formatPercent(number)}</span>
        <Progress
          percent={Math.min(Math.max(number, 0), 100)}
          showInfo={false}
        />
      </Space>
    );
  };

  const reasonColumns: ColumnsType<ICustomerSatisfactionReasonStatistic> = [
    {
      title: t("reason"),
      dataIndex: "reasonName",
      key: "reasonName",
      width: 220,
      render: (reasonName, record) => (
        <Space direction="vertical" size={2}>
          <Typography.Text strong>
            {record.missingReason ? t("missing_reason") : (reasonName ?? "-")}
          </Typography.Text>
          {record.smallSample && (
            <Tag color="warning" icon={<WarningOutlined />}>
              {t("small_sample")}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: t("configured_signals"),
      dataIndex: "configuredOperationalSignals",
      key: "configuredOperationalSignals",
      width: 260,
      render: (signals) => (
        <OperationalSignalTags signals={signals} size="small" />
      ),
    },
    {
      title: t("unsatisfied_orders"),
      dataIndex: "unsatisfiedCount",
      key: "unsatisfiedCount",
      align: "center",
      width: 130,
      render: formatNumber,
    },
    {
      title: t("share"),
      dataIndex: "shareOfUnsatisfiedRate",
      key: "shareOfUnsatisfiedRate",
      align: "center",
      width: 120,
      render: renderRate,
    },
    {
      title: t("match_rate"),
      dataIndex: "matchedSignalOrderRate",
      key: "matchedSignalOrderRate",
      align: "center",
      width: 150,
      render: renderRate,
    },
    {
      title: t("mismatch_count"),
      dataIndex: "unmatchedConfiguredSignalOrderCount",
      key: "unmatchedConfiguredSignalOrderCount",
      align: "center",
      width: 140,
      render: formatNumber,
    },
    {
      title: t("average_preparing_minutes"),
      dataIndex: "averagePreparingMinutes",
      key: "averagePreparingMinutes",
      align: "center",
      width: 150,
      render: formatMinutes,
    },
    {
      title: t("average_delivery_minutes"),
      dataIndex: "averageDeliveryMinutes",
      key: "averageDeliveryMinutes",
      align: "center",
      width: 150,
      render: formatMinutes,
    },
  ];

  const servicePointColumns: ColumnsType<ICustomerSatisfactionServicePointStatistic> =
    [
      {
        title: t("service_point"),
        key: "servicePoint",
        width: 260,
        render: (_, record) => (
          <Space direction="vertical" size={0}>
            <Typography.Text strong>{record.areaName ?? "-"}</Typography.Text>
            <Typography.Text type="secondary">
              {record.storeName ?? "-"}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: t("period_orders"),
        dataIndex: "periodOrderCount",
        key: "periodOrderCount",
        align: "center",
        render: formatNumber,
      },
      {
        title: t("coverage_rate"),
        dataIndex: "coverageRate",
        key: "coverageRate",
        align: "center",
        render: renderRate,
      },
      {
        title: t("dissatisfaction_rate"),
        dataIndex: "dissatisfactionRate",
        key: "dissatisfactionRate",
        align: "center",
        render: renderRate,
      },
      {
        title: t("no_response_orders"),
        dataIndex: "noResponseCount",
        key: "noResponseCount",
        align: "center",
        render: formatNumber,
      },
      {
        title: t("no_response_rate"),
        dataIndex: "noResponseRate",
        key: "noResponseRate",
        align: "center",
        render: renderRate,
      },
      {
        title: t("product_issue_orders"),
        dataIndex: "productIssueOrderCount",
        key: "productIssueOrderCount",
        align: "center",
        render: formatNumber,
      },
      {
        title: t("perfect_product_order_rate"),
        dataIndex: "perfectProductOrderRate",
        key: "perfectProductOrderRate",
        align: "center",
        render: renderRate,
      },
    ];

  const actorColumns = (
    nameKey: "driverName" | "investorName",
  ): ColumnsType<ICustomerSatisfactionActorStatistic> => [
    {
      title: nameKey === "driverName" ? t("driver") : t("investor"),
      dataIndex: nameKey,
      key: nameKey,
      width: 220,
      render: (name, record) => (
        <Space direction="vertical" size={2}>
          <Typography.Text strong>{name ?? "-"}</Typography.Text>
          {record.smallSample && <Tag color="warning">{t("small_sample")}</Tag>}
        </Space>
      ),
    },
    {
      title: t("unsatisfied_orders"),
      dataIndex: "unsatisfiedCount",
      key: "unsatisfiedCount",
      align: "center",
      render: formatNumber,
    },
    {
      title: t("delivery_timing_orders"),
      dataIndex: "deliveryTimingOrderCount",
      key: "deliveryTimingOrderCount",
      align: "center",
      render: (value, record) =>
        formatNumber(value ?? record.deliveryTimingSignalCount),
    },
    {
      title: t("product_availability_orders"),
      dataIndex: "productAvailabilityOrderCount",
      key: "productAvailabilityOrderCount",
      align: "center",
      render: (value, record) =>
        formatNumber(value ?? record.productAvailabilitySignalCount),
    },
    {
      title: t("preparation_timing_orders"),
      dataIndex: "preparationTimingOrderCount",
      key: "preparationTimingOrderCount",
      align: "center",
      render: (value, record) =>
        formatNumber(value ?? record.preparationTimingSignalCount),
    },
    {
      title: t("match_rate"),
      dataIndex: "matchedSignalOrderRate",
      key: "matchedSignalOrderRate",
      align: "center",
      render: renderRate,
    },
  ];

  const signalColumns: ColumnsType<ICustomerSatisfactionOperationalSignalStatistic> =
    [
      {
        title: t("operational_signal"),
        key: "key",
        width: 240,
        render: (_, record) => (
          <OperationalSignalTags signals={[record]} size="small" />
        ),
      },
      {
        title: t("orders"),
        dataIndex: "orderCount",
        key: "orderCount",
        align: "center",
        render: (value, record) => formatNumber(value ?? record.count),
      },
      {
        title: t("unsatisfied_orders"),
        dataIndex: "unsatisfiedCount",
        key: "unsatisfiedCount",
        align: "center",
        render: formatNumber,
      },
      {
        title: t("share"),
        dataIndex: "shareOfUnsatisfiedRate",
        key: "shareOfUnsatisfiedRate",
        align: "center",
        render: (value, record) => renderRate(value ?? record.rate),
      },
      {
        title: t("matched_reason_orders"),
        dataIndex: "matchedReasonOrderCount",
        key: "matchedReasonOrderCount",
        align: "center",
        render: formatNumber,
      },
    ];

  const orderColumns: ColumnsType<ICustomerSatisfactionUnsatisfiedOrder> = [
    {
      title: t("order_number"),
      dataIndex: "orderNumber",
      key: "orderNumber",
      fixed: "left",
      width: 130,
      render: (orderNumber, record) =>
        record.orderId ? (
          <Link
            className={styles.orderLink}
            to={`/orders/details/${record.orderId}`}
          >
            {orderNumber ?? record.orderId}
          </Link>
        ) : (
          (orderNumber ?? "-")
        ),
    },
    {
      title: t("date"),
      dataIndex: "orderDate",
      key: "orderDate",
      width: 120,
    },
    {
      title: t("reason"),
      dataIndex: "reasonName",
      key: "reasonName",
      width: 220,
      render: (reasonName) => reasonName ?? t("missing_reason"),
    },
    {
      title: t("configured_signals"),
      dataIndex: "reasonOperationalSignals",
      key: "reasonOperationalSignals",
      width: 240,
      render: (signals) => (
        <OperationalSignalTags signals={signals} size="small" />
      ),
    },
    {
      title: t("actual_signals"),
      dataIndex: "signalKeys",
      key: "signalKeys",
      width: 240,
      render: (signals) => (
        <OperationalSignalTags signals={signals} size="small" />
      ),
    },
    {
      title: t("matched_signals"),
      dataIndex: "matchedSignalKeys",
      key: "matchedSignalKeys",
      width: 220,
      render: (signals) => (
        <OperationalSignalTags
          signals={signals}
          size="small"
          emptyTextKey="not_matched_with_indicators"
        />
      ),
    },
    {
      title: t("unmatched_configured_signals"),
      dataIndex: "unmatchedReasonSignalKeys",
      key: "unmatchedReasonSignalKeys",
      width: 240,
      render: (signals) => (
        <OperationalSignalTags signals={signals} size="small" />
      ),
    },
    {
      title: t("unexpected_signals"),
      dataIndex: "unexpectedSignalKeys",
      key: "unexpectedSignalKeys",
      width: 240,
      render: (signals) => (
        <OperationalSignalTags signals={signals} size="small" />
      ),
    },
    {
      title: t("preparing_minutes"),
      dataIndex: "preparingMinutes",
      key: "preparingMinutes",
      align: "center",
      width: 130,
      render: formatMinutes,
    },
    {
      title: t("delivery_minutes"),
      dataIndex: "deliveryMinutes",
      key: "deliveryMinutes",
      align: "center",
      width: 130,
      render: formatMinutes,
    },
    {
      title: t("shortage_value"),
      dataIndex: "shortageValue",
      key: "shortageValue",
      align: "center",
      width: 130,
      render: formatMoney,
    },
    {
      title: t("driver"),
      dataIndex: "driverName",
      key: "driverName",
      width: 160,
      render: (value) => value ?? "-",
    },
    {
      title: t("investor"),
      dataIndex: "investorName",
      key: "investorName",
      width: 160,
      render: (value) => value ?? "-",
    },
    {
      title: t("note"),
      dataIndex: "note",
      key: "note",
      width: 320,
      render: (value) => <div className={styles.note}>{value ?? "-"}</div>,
    },
  ];

  const timeSeriesColumns: ColumnsType<ICustomerSatisfactionTimeSeriesItem> = [
    {
      title: t("date"),
      dataIndex: "date",
      key: "date",
      render: (value, record) => value ?? record.label ?? "-",
    },
    {
      title: t("evaluated_orders"),
      dataIndex: "evaluatedOrderCount",
      key: "evaluatedOrderCount",
      align: "center",
      render: formatNumber,
    },
    {
      title: t("no_response_orders"),
      dataIndex: "noResponseCount",
      key: "noResponseCount",
      align: "center",
      render: formatNumber,
    },
    {
      title: t("satisfaction_rate"),
      dataIndex: "satisfactionRate",
      key: "satisfactionRate",
      align: "center",
      render: renderRate,
    },
    {
      title: t("dissatisfaction_rate"),
      dataIndex: "dissatisfactionRate",
      key: "dissatisfactionRate",
      align: "center",
      render: renderRate,
    },
    {
      title: t("no_response_rate"),
      dataIndex: "noResponseRate",
      key: "noResponseRate",
      align: "center",
      render: renderRate,
    },
    {
      title: t("match_rate"),
      dataIndex: "reasonSignalMatchedOrderRate",
      key: "reasonSignalMatchedOrderRate",
      align: "center",
      render: renderRate,
    },
  ];

  const tableLocale = {
    emptyText: <Empty description={t("no_data")} />,
  };
  const cacheMeta: IStatisticCacheMeta | undefined = statsState.cacheMeta;
  const generatedAt = formatCacheDate(cacheMeta?.generatedAt);
  const queuedAt = formatCacheDate(cacheMeta?.queuedAt);
  const retryAfter = formatDuration(cacheMeta?.retryAfterSeconds);
  const refreshInterval = formatDuration(cacheMeta?.refreshIntervalSeconds);

  return (
    <div className={styles.container}>
      <Card className={styles.panel} bodyStyle={{ padding: 16 }}>
        <div className={styles.toolbar}>
          <Space direction="vertical" size={0}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              {t("customer_satisfaction_statistics")}
            </Typography.Title>
            <div className={styles.period}>
              <span>{t("customer_satisfaction_statistics_subtitle")}</span>
              {stats?.methodology?.preparationDelayWarningMinutes && (
                <Tag color="geekblue">
                  {`${t("preparation_warning_minutes")}: ${formatMinutes(
                    stats.methodology.preparationDelayWarningMinutes,
                  )}`}
                </Tag>
              )}
              {stats?.methodology?.deliveryDelayWarningMinutes && (
                <Tag color="purple">
                  {`${t("delivery_warning_minutes")}: ${formatMinutes(
                    stats.methodology.deliveryDelayWarningMinutes,
                  )}`}
                </Tag>
              )}
            </div>
          </Space>
          <div className={styles.filters}>
            <Form.Item
              style={{ marginBottom: 0 }}
              validateStatus={
                fieldErrors.startDate || fieldErrors.endDate
                  ? "error"
                  : undefined
              }
              help={fieldErrors.startDate || fieldErrors.endDate}
            >
              <RangePicker
                allowClear
                format={DATE_FORMAT}
                placeholder={[t("startDate"), t("endDate")]}
                disabledDate={disabledFutureDate}
                value={[
                  startDate ? dayjs(startDate) : null,
                  endDate ? dayjs(endDate) : null,
                ]}
                onChange={(_, values) => {
                  setStartDate(values[0] || undefined);
                  setEndDate(values[1] || undefined);
                }}
              />
            </Form.Item>
            <div className={styles.filterActions}>
              {filtersDirty ? (
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  loading={loading || isPending}
                  onClick={applyFilters}
                >
                  {t("apply")}
                </Button>
              ) : (
                <Tooltip title={t("refresh")}>
                  <RefreshBtn
                    loading={loading || isPending}
                    onClick={applyFilters}
                  >
                    {t("refresh")}
                  </RefreshBtn>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
        {(cacheMeta || isPending || statsState.message) && (
          <div className={styles.cacheMetaBar}>
            {isPending && (
              <Tag color="processing">{t("statistics_data_preparing")}</Tag>
            )}
            {generatedAt && (
              <span>
                {t("statistics_last_generated_at", { value: generatedAt })}
              </span>
            )}
            {queuedAt && isPending && (
              <span>{t("statistics_queued_at", { value: queuedAt })}</span>
            )}
            {retryAfter && isPending && (
              <span>{t("statistics_retry_after", { value: retryAfter })}</span>
            )}
            {refreshInterval && (
              <span>
                {t("statistics_refresh_interval", { value: refreshInterval })}
              </span>
            )}
          </div>
        )}

        {statsState.message && (
          <Alert
            style={{ marginTop: 12 }}
            type="error"
            showIcon
            message={statsState.message}
          />
        )}

        {isPending && (
          <Alert
            style={{ marginTop: 12 }}
            type="info"
            showIcon
            message={
              hasReadyStats
                ? t("statistics_data_updating_message")
                : t("statistics_data_pending_message")
            }
          />
        )}

        {hasSmallSamples && (
          <Alert
            style={{ marginTop: 12 }}
            type="warning"
            showIcon
            message={t("small_sample_warning")}
          />
        )}

        {isInitialLoading || (isPending && !hasReadyStats) ? (
          <div className={styles.skeletonPanel}>
            <Skeleton active paragraph={{ rows: 8 }} />
          </div>
        ) : hasReadyStats ? (
          <>
            <div className={styles.metrics}>
              <Row gutter={[12, 12]}>
                <Col xs={24} sm={12} lg={8} xl={4}>
                  <Statistic
                    title={t("satisfaction_rate")}
                    value={formatPercent(summary?.satisfactionRate)}
                    prefix={<CheckCircleOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} lg={8} xl={4}>
                  <Statistic
                    title={t("dissatisfaction_rate")}
                    value={formatPercent(summary?.dissatisfactionRate)}
                    prefix={<WarningOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} lg={8} xl={4}>
                  <Statistic
                    title={t("no_response_rate")}
                    value={formatPercent(summary?.noResponseRate)}
                    prefix={<FileSearchOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} lg={8} xl={4}>
                  <Statistic
                    title={t("evaluation_coverage")}
                    value={formatPercent(summary?.coverageRate)}
                    prefix={<FileSearchOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} lg={8} xl={4}>
                  <Statistic
                    title={t("reason_signal_match_rate")}
                    value={formatPercent(summary?.reasonSignalMatchedOrderRate)}
                  />
                </Col>
                <Col xs={24} sm={12} lg={8} xl={4}>
                  <Statistic
                    title={t("missing_reason_rate")}
                    value={formatPercent(summary?.missingReasonRate)}
                  />
                </Col>
                <Col xs={24} sm={12} lg={8} xl={4}>
                  <Statistic
                    title={t("product_completion_rate")}
                    value={formatPercent(summary?.perfectProductOrderRate)}
                  />
                </Col>
                <Col xs={24} sm={12} lg={8} xl={4}>
                  <Statistic
                    title={t("unsatisfied_orders")}
                    value={formatNumber(summary?.unsatisfiedCount)}
                  />
                </Col>
                <Col xs={24} sm={12} lg={8} xl={4}>
                  <Statistic
                    title={t("no_response_orders")}
                    value={formatNumber(summary?.noResponseCount)}
                  />
                </Col>
              </Row>
            </div>

            <div className={styles.tabs}>
              <Tabs
                items={[
                  {
                    key: "reasons",
                    label: t("dissatisfaction_reasons"),
                    children: (
                      <Table
                        rowKey={(record, index) =>
                          `${record.reasonId ?? "missing"}-${index}`
                        }
                        columns={reasonColumns}
                        dataSource={stats?.reasons || []}
                        loading={loading}
                        size="small"
                        scroll={{ x: 1280 }}
                        pagination={{ pageSize: 6 }}
                        locale={tableLocale}
                      />
                    ),
                  },
                  {
                    key: "operations",
                    label: t("operations"),
                    children: (
                      <Tabs
                        items={[
                          {
                            key: "servicePoints",
                            label: t("service_points"),
                            children: (
                              <Table
                                rowKey={(record, index) =>
                                  `${record.areaId ?? "area"}-${record.storeId ?? index}`
                                }
                                columns={servicePointColumns}
                                dataSource={stats?.servicePoints || []}
                                loading={loading}
                                size="small"
                                scroll={{ x: 1240 }}
                                pagination={{ pageSize: 6 }}
                                locale={tableLocale}
                              />
                            ),
                          },
                          {
                            key: "drivers",
                            label: t("drivers"),
                            children: (
                              <Table
                                rowKey={(record, index) =>
                                  `${record.driverId ?? "driver"}-${index}`
                                }
                                columns={actorColumns("driverName")}
                                dataSource={stats?.drivers || []}
                                loading={loading}
                                size="small"
                                scroll={{ x: 900 }}
                                pagination={{ pageSize: 6 }}
                                locale={tableLocale}
                              />
                            ),
                          },
                          {
                            key: "investors",
                            label: t("investors"),
                            children: (
                              <Table
                                rowKey={(record, index) =>
                                  `${record.investorId ?? "investor"}-${index}`
                                }
                                columns={actorColumns("investorName")}
                                dataSource={stats?.investors || []}
                                loading={loading}
                                size="small"
                                scroll={{ x: 900 }}
                                pagination={{ pageSize: 6 }}
                                locale={tableLocale}
                              />
                            ),
                          },
                          {
                            key: "signals",
                            label: t("operational_signals"),
                            children: (
                              <Table
                                rowKey={(record, index) =>
                                  `${record.key}-${index}`
                                }
                                columns={signalColumns}
                                dataSource={stats?.operationalSignals || []}
                                loading={loading}
                                size="small"
                                scroll={{ x: 760 }}
                                pagination={{ pageSize: 6 }}
                                locale={tableLocale}
                              />
                            ),
                          },
                        ]}
                      />
                    ),
                  },
                  {
                    key: "orders",
                    label: t("unsatisfied_order_details"),
                    children: (
                      <Table
                        rowKey={(record, index) => `${record.orderId}-${index}`}
                        columns={orderColumns}
                        dataSource={stats?.unsatisfiedOrders || []}
                        loading={loading}
                        size="small"
                        scroll={{ x: 2300 }}
                        pagination={{ pageSize: 8 }}
                        locale={tableLocale}
                      />
                    ),
                  },
                  {
                    key: "timeSeries",
                    label: t("daily_progress"),
                    children: (
                      <Table
                        rowKey={(record, index) =>
                          `${record.date ?? record.label}-${index}`
                        }
                        columns={timeSeriesColumns}
                        dataSource={stats?.timeSeries || []}
                        loading={loading}
                        size="small"
                        scroll={{ x: 900 }}
                        pagination={{ pageSize: 8 }}
                        locale={tableLocale}
                      />
                    ),
                  },
                ]}
              />
            </div>
          </>
        ) : null}
      </Card>
    </div>
  );
};

export default CustomerSatisfactionStats;
